import React from "react";
import {
    Eye,
    Info,
    Monitor,
    Receipt,
    Shield,
    User,
    Users,
    type LucideIcon,
} from "lucide-react";

import { Badge, Button } from "../../../../ui/index.ts";
import { Card } from "../../../../shared/card/card.tsx";
import type { OffboardingRequest } from "../../schemas/offboarding-request.schema.ts";
import styles from "./clearance-summary.module.css";

export type ClearanceDepartment = "IT" | "PAYROLL" | "HR" | "ADMIN" | "RM";

interface ClearanceSummaryProps {
    offboarding: OffboardingRequest;
    showActions?: boolean;
    /** Departments the current user may submit clearance for. */
    viewerDepartments?: ClearanceDepartment[];
    /** When true (HR), show view-form on all completed department rows. */
    canViewAllCompleted?: boolean;
    allOtherDeptsComplete?: boolean;
    exitInterviewAcknowledged?: boolean;
    actionsAvailable?: boolean;
    actionUnavailableMessage?: string;
    onViewForm?: (department: ClearanceDepartment) => void;
    onTakeAction?: (department: ClearanceDepartment) => void;
}

type DepartmentConfig = {
    key: ClearanceDepartment;
    label: string;
    icon: LucideIcon;
    completed: (request: OffboardingRequest) => boolean;
    remarks: (request: OffboardingRequest) => string | null | undefined;
};

const DEPARTMENTS: DepartmentConfig[] = [
    {
        key: "IT",
        label: "IT",
        icon: Monitor,
        completed: (request) => request.itCompleted,
        remarks: (request) => request.itRemarks,
    },
    {
        key: "PAYROLL",
        label: "Payroll",
        icon: Receipt,
        completed: (request) => request.payrollCompleted,
        remarks: (request) => request.payrollRemarks,
    },
    {
        key: "ADMIN",
        label: "Admin",
        icon: Shield,
        completed: (request) => request.adminCompleted,
        remarks: (request) => request.adminRemarks,
    },
    {
        key: "RM",
        label: "Reporting Manager",
        icon: User,
        completed: (request) => request.rmCompleted,
        remarks: (request) => request.rmRemarks,
    },
    {
        key: "HR",
        label: "HR",
        icon: Users,
        completed: (request) => request.hrCompleted,
        remarks: (request) => request.hrRemarks,
    },
];

function formatRemarks(value: string | null | undefined): string {
    if (value == null || value.trim() === "") {
        return "—";
    }

    return value;
}

export function ClearanceSummary({
    offboarding,
    showActions = false,
    viewerDepartments = [],
    canViewAllCompleted = false,
    allOtherDeptsComplete = false,
    exitInterviewAcknowledged = false,
    actionsAvailable = true,
    actionUnavailableMessage = "Clearance actions are not available yet.",
    onViewForm,
    onTakeAction,
}: ClearanceSummaryProps) {
    const gridClass = showActions ? styles.gridFull : styles.gridEmployee;

    const renderActionCell = (
        department: ClearanceDepartment,
        isCompleted: boolean,
    ) => {
        if (!showActions) return null;

        if (isCompleted) {
            if (canViewAllCompleted || viewerDepartments.includes(department)) {
                return (
                    <div className={styles.actions} role="cell">
                        <button
                            type="button"
                            className={styles.viewBtn}
                            aria-label={`View ${department} clearance form`}
                            onClick={() => onViewForm?.(department)}
                        >
                            <Eye size={16} strokeWidth={2} aria-hidden />
                            <span>View</span>
                        </button>
                    </div>
                );
            }

            return (
                <span className={styles.actionDash} role="cell">
                    —
                </span>
            );
        }

        if (!viewerDepartments.includes(department)) {
            return (
                <span className={styles.actionDash} role="cell">
                    —
                </span>
            );
        }

        const isHrRow = department === "HR";
        const takeActionDisabled =
            !actionsAvailable ||
            (isHrRow && (!allOtherDeptsComplete || !exitInterviewAcknowledged));

        return (
          <div className={styles.actions} role="cell">
            <Button
              variant="accent"
              size="sm"
              disabled={takeActionDisabled}
              title={
                !actionsAvailable
                  ? actionUnavailableMessage
                  : isHrRow && !allOtherDeptsComplete
                    ? "HR clearance can be submitted after the exit interview form is completed."
                    : isHrRow && !exitInterviewAcknowledged
                      ? "HR clearance can be submitted after the exit interview form is acknowledged."
                    : undefined
              }
              onClick={() => onTakeAction?.(department)}
            >
              Take Action
            </Button>
          </div>
        );
    };

    return (
        <Card className={styles.card}>
            <div className={styles.header}>
                <h3 className="heading-sm">Clearance Summary</h3>
            </div>

            {showActions && !actionsAvailable && (
                <div className={styles.actionNotice} role="status">
                    <Info
                        className={styles.actionNoticeIcon}
                        size={18}
                        strokeWidth={2}
                        aria-hidden
                    />
                    <p className="body-sm">{actionUnavailableMessage}</p>
                </div>
            )}

            <div className={styles.table} role="table" aria-label="Clearance summary">
                <div
                    className={`${styles.tableHeader} ${gridClass}`}
                    role="row"
                >
                    <span role="columnheader">Department</span>
                    <span role="columnheader">Status</span>
                    {showActions ? (
                        <>

                            <span role="columnheader">Action</span>
                        </>
                    ) : null}
                </div>

                {DEPARTMENTS.map((department) => {
                    const Icon = department.icon;
                    const isCompleted = department.completed(offboarding);


                    return (
                        <div
                            key={department.key}
                            className={`${styles.tableRow} ${gridClass}`}
                            role="row"
                        >
                            <div className={styles.departmentCell} role="cell">
                                <span className={styles.deptIcon} aria-hidden>
                                    <Icon size={16} strokeWidth={2} />
                                </span>
                                <span className={styles.departmentLabel}>
                                    {department.label}
                                </span>
                            </div>

                            <div role="cell">
                                <Badge variant={isCompleted ? "success" : "warning"}>
                                    {isCompleted ? "Completed" : "Pending"}
                                </Badge>
                            </div>

                            {showActions ? (
                                <>

                                    {renderActionCell(department.key, isCompleted)}
                                </>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
